import { useState } from 'react';
import { useQuery, useApolloClient, gql } from '@apollo/client';
import { exportMultiSheetExcel, exportToExcel } from '../../utils/exportToExcel';
import {
  AreaChart, Area,
  BarChart as RBarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Users, CreditCard, Tag, QrCode, TrendingUp, Banknote,
  ShieldAlert, Loader2, UserCheck, UserX, UserMinus,
  AlertTriangle, MapPin, GitBranch, ChevronDown, Download,
} from 'lucide-react';
import UserDetailModal from '../../components/admin/users/UserDetailModal';

// ── GraphQL Queries ──────────────────────────────────────────────────────────

const GET_USERS_STATISTICS = gql`
  query GetUsersStatistics {
    usersStatistics {
      total active inactive verified unverified
      byType { PHARMACIST LABORATORY ADMIN SELLER }
    }
  }
`;

const GET_TRANSACTION_STATISTICS = gql`
  query GetTransactionStatistics($dateFrom: String, $dateTo: String) {
    transactionStatistics(dateFrom: $dateFrom, dateTo: $dateTo) {
      total validated pending rejected scans withdrawals
      totalGains totalCommissions thisMonth thisMonthGains
    }
  }
`;

const GET_RECENT_TRANSACTIONS = gql`
  query GetRecentTransactions {
    transactions {
      id gain status type createdAt
      user { firstName lastName email }
      pharmacy { namePharmacy }
    }
  }
`;

const GET_PROMOTIONS_SUMMARY = gql`
  query GetPromotionsSummary {
    allPromotions { id status }
  }
`;

const GET_PAYMENT_REQUESTS_SUMMARY = gql`
  query GetPaymentRequestsSummary {
    paymentRequests {
      id amount status dateDemande
      user { firstName lastName }
    }
  }
`;

const GET_TOP_SELLERS = gql`
  query GetTopSellers($input: UsersQueryInput) {
    usersPaginated(usersQueryInput: $input) {
      users {
        id firstName lastName email commissionBalance
        pharmacy { namePharmacy region { nomRegion } }
      }
    }
  }
`;

const GET_TOP_PHARMACIES = gql`
  query GetTopPharmacies {
    topPharmaciesByScans(limit: 8) {
      id namePharmacy scanCount totalGains
    }
  }
`;

const GET_DAILY_STATS = gql`
  query GetDailyStats($dateFrom: String, $dateTo: String) {
    dailyTransactionStats(dateFrom: $dateFrom, dateTo: $dateTo) {
      day count totalGain totalCommission
    }
  }
`;

const GET_MONTHLY_USERS = gql`
  query GetMonthlyUsers($months: Int) {
    monthlyUserRegistrations(months: $months) {
      month total SELLER PHARMACIST LABORATORY
    }
  }
`;

const GET_PENDING_FRAUD_COUNT = gql`
  query GetPendingFraudCount {
    pendingFraudCasesCount
  }
`;

const GET_USER_CHURN_METRICS = gql`
  query GetUserChurnMetrics {
    userChurnMetrics { active atRisk churned ghost total churnRate }
  }
`;

const GET_USERS_BY_REGION = gql`
  query GetUsersByRegion {
    usersByRegion {
      region totalUsers activeUsers churnedUsers ghostUsers
      totalScans totalGains churnRate
    }
  }
`;

const GET_CHURN_PROFILE = gql`
  query GetChurnProfile {
    churnProfileByType {
      userType count avgScansBeforeChurn avgGainBeforeChurn avgDaysActive
    }
  }
`;

const GET_RETENTION_COHORTS = gql`
  query GetRetentionCohorts($months: Int) {
    retentionCohorts(months: $months) {
      cohortMonth cohortSize monthlyRetention
    }
  }
`;

const GET_ALL_REFERRALS = gql`
  query GetAllReferrals {
    allReferrals {
      id referralDate firstScanBonusPaid scanCount
      referrer { id firstName lastName email }
      referred { id firstName lastName }
    }
  }
`;

const GET_USERS_BY_CHURN_STATUS = gql`
  query GetUsersByChurnStatus($status: String!) {
    usersByChurnStatus(status: $status) {
      total
      users {
        id email firstName lastName tel userType
        commissionBalance isActive isPhoneVerified
        createdAt lastLogin lastScan scanCount
      }
    }
  }
`;

// ── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: "Vue d'ensemble" },
  { id: 'transactions', label: 'Transactions & Revenus' },
  { id: 'users', label: 'Utilisateurs & Croissance' },
  { id: 'retention', label: 'Rétention & Géographie' },
  { id: 'promotions', label: 'Promotions & Paiements' },
];

const FILTER_MODES = [
  { key: 'all', label: 'Tout' },
  { key: 'preset', label: 'Rapide' },
  { key: 'custom', label: 'Dates' },
  { key: 'quarter', label: 'Trimestre' },
];

const PRESETS = [
  { label: "Auj", days: 1 },
  { label: "7j", days: 7 },
  { label: "30j", days: 30 },
  { label: "90j", days: 90 },
  { label: "1an", days: 365 },
];

const QUARTERS = [
  { label: 'Q1', months: [0, 1, 2] },
  { label: 'Q2', months: [3, 4, 5] },
  { label: 'Q3', months: [6, 7, 8] },
  { label: 'Q4', months: [9, 10, 11] },
];

const C = {
  green: '#10b981',
  purple: '#8b5cf6',
  blue: '#3b82f6',
  indigo: '#6366f1',
  orange: '#f97316',
  red: '#ef4444',
  yellow: '#f59e0b',
  gray: '#94a3b8',
};

const USER_TYPE_COLORS = {
  SELLER: C.orange,
  PHARMACIST: C.green,
  LABORATORY: C.purple,
  ADMIN: C.indigo,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtCurrency = (v) =>
  new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD', maximumFractionDigits: 2 }).format(v ?? 0);
const fmtNum = (v) => new Intl.NumberFormat('fr-MA').format(v ?? 0);
const fmtPct = (v) => `${(v ?? 0).toFixed(1)}%`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR') : '—';

const shortDay = (day) =>
  /^\d{4}-\d{2}-\d{2}$/.test(day) ? `${day.slice(8)}/${day.slice(5, 7)}` : day;

const retentionBg = (pct) => {
  if (pct >= 70) return '#bbf7d0';
  if (pct >= 50) return '#d1fae5';
  if (pct >= 30) return '#fef9c3';
  if (pct >= 10) return '#fed7aa';
  if (pct > 0) return '#fee2e2';
  return '#f3f4f6';
};
const retentionFg = (pct) => {
  if (pct >= 70) return '#166534';
  if (pct >= 50) return '#065f46';
  if (pct >= 30) return '#854d0e';
  if (pct >= 10) return '#9a3412';
  if (pct > 0) return '#991b1b';
  return '#9ca3af';
};

const inDateRange = (dateStr, dateFrom, dateTo) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (dateFrom && d < new Date(dateFrom)) return false;
  if (dateTo && d > new Date(dateTo + 'T23:59:59')) return false;
  return true;
};

// Compute { dateFrom, dateTo } from filter state
const computeDateRange = (filterMode, presetDays, customFrom, customTo, qNum, qYear) => {
  if (filterMode === 'all') return { dateFrom: undefined, dateTo: undefined };
  if (filterMode === 'custom') {
    return { dateFrom: customFrom || undefined, dateTo: customTo || undefined };
  }
  if (filterMode === 'quarter' && qNum) {
    const mStart = (qNum - 1) * 3;
    const d1 = new Date(qYear, mStart, 1);
    const d2 = new Date(qYear, mStart + 3, 0);
    return { dateFrom: d1.toISOString().slice(0, 10), dateTo: d2.toISOString().slice(0, 10) };
  }
  const now = new Date();
  const dateTo = now.toISOString().slice(0, 10);
  if (presetDays === 1) return { dateFrom: dateTo, dateTo };
  const d = new Date(now.getTime() - (presetDays - 1) * 86400000);
  return { dateFrom: d.toISOString().slice(0, 10), dateTo };
};

const describePeriod = (filterMode, presetDays, customFrom, customTo, qNum, qYear) => {
  if (filterMode === 'all') return 'Toute la période';
  if (filterMode === 'custom') {
    if (customFrom && customTo) return `${fmtDate(customFrom)} → ${fmtDate(customTo)}`;
    if (customFrom) return `À partir du ${fmtDate(customFrom)}`;
    if (customTo) return `Jusqu'au ${fmtDate(customTo)}`;
    return 'Dates non définies';
  }
  if (filterMode === 'quarter' && qNum) {
    const labels = ['Q1 (Jan–Mar)', 'Q2 (Avr–Jun)', 'Q3 (Jul–Sep)', 'Q4 (Oct–Déc)'];
    return `${labels[qNum - 1]} ${qYear}`;
  }
  return { 1: "Aujourd'hui", 7: '7 derniers jours', 30: '30 derniers jours', 90: '90 derniers jours', 365: 'Dernière année' }[presetDays] ?? `${presetDays} jours`;
};

// ── Shared UI ────────────────────────────────────────────────────────────────

const Spinner = () => (
  <div className="flex items-center justify-center h-48">
    <Loader2 className="h-7 w-7 animate-spin text-gray-300" />
  </div>
);

const EmptyState = ({ message = 'Aucune donnée disponible' }) => (
  <div className="flex items-center justify-center h-48 text-sm text-gray-400">{message}</div>
);

const ChartTooltip = ({ active, payload, label, valFmt }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-medium text-gray-700 mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
          <span className="text-gray-500">{p.name}</span>
          <span className="font-semibold text-gray-900 ml-auto pl-4">
            {valFmt ? valFmt(p.value, p.name) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const KpiCard = ({ icon: Icon, iconBg, iconColor, label, value, sub, loading, accentClass }) => (
  <div className={`bg-white rounded-xl shadow-sm p-3 sm:p-5 ${accentClass ?? ''}`}>
    <div className={`${iconBg} rounded-lg p-2 sm:p-2.5 w-fit`}>
      <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${iconColor}`} />
    </div>
    <div className="mt-2 sm:mt-3">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide leading-tight">{label}</p>
      <p className="mt-1 text-xl sm:text-2xl font-bold text-gray-900">
        {loading ? <Loader2 className="h-5 w-5 animate-spin text-gray-300 mt-1" /> : value}
      </p>
      {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
    </div>
  </div>
);

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-xl shadow-sm overflow-hidden">
    {(title || subtitle) && (
      <div className="px-5 py-4 border-b border-gray-100">
        {title && <h3 className="text-sm font-semibold text-gray-900">{title}</h3>}
        {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    )}
    {children}
  </div>
);

// ── Filter Bar ────────────────────────────────────────────────────────────────

const FilterBar = ({
  filterMode, onModeChange, presetDays, onPresetDays,
  customFrom, onCustomFrom, customTo, onCustomTo,
  qNum, onQNum, qYear, onQYear,
}) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const period = describePeriod(filterMode, presetDays, customFrom, customTo, qNum, qYear);

  return (
    <div className="bg-white rounded-xl shadow-sm p-3 mb-6">
      {/* Row 1: mode selector + active badge */}
      <div className="flex items-center gap-1 flex-wrap">
        <div className="flex flex-wrap gap-1">
          {FILTER_MODES.map((m) => (
            <button
              key={m.key}
              onClick={() => onModeChange(m.key)}
              className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                filterMode === m.key
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        {/* Active period badge — always visible */}
        <div className="ml-auto flex items-center gap-1.5">
          <span className="hidden sm:inline text-xs text-gray-400">Filtre actif :</span>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${
            filterMode === 'all' ? 'bg-gray-100 text-gray-600' : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
          }`}>
            {period}
          </span>
        </div>
      </div>

      {/* Row 2: mode-specific controls (only shown when not 'all') */}
      {filterMode !== 'all' && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          {filterMode === 'preset' && (
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              {PRESETS.map((p) => (
                <button key={p.days} onClick={() => onPresetDays(p.days)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                    presetDays === p.days ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
          )}

          {filterMode === 'custom' && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium text-gray-500">Du</span>
              <input type="date" value={customFrom} onChange={(e) => onCustomFrom(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-gray-900 focus:outline-none" />
              <span className="text-xs font-medium text-gray-500">Au</span>
              <input type="date" value={customTo} onChange={(e) => onCustomTo(e.target.value)}
                className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:ring-2 focus:ring-gray-900 focus:outline-none" />
              {(customFrom || customTo) && (
                <button onClick={() => { onCustomFrom(''); onCustomTo(''); }}
                  className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100">
                  Effacer
                </button>
              )}
            </div>
          )}

          {filterMode === 'quarter' && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {QUARTERS.map((q, i) => (
                  <button key={q.label} onClick={() => onQNum(i + 1)}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                      qNum === i + 1 ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}>
                    {q.label}
                  </button>
                ))}
              </div>
              <select value={qYear} onChange={(e) => onQYear(Number(e.target.value))}
                className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 bg-white">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Tab: Vue d'ensemble ───────────────────────────────────────────────────────

const OverviewTab = ({
  usersStats, txStats, allTransactions, allPaymentRequests, allPromotions,
  usersLoading, txStatsLoading, txLoading, paymentsLoading, promotionsLoading,
  dateRange,
}) => {
  const pendingCount = allPaymentRequests.filter((p) => p.status === 'PENDING').length;
  const activePromos = allPromotions.filter((p) => p.status === 'ACTIVE').length;

  const recentTx = [...allTransactions]
    .filter((t) => inDateRange(t.createdAt, dateRange.dateFrom, dateRange.dateTo))
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 6);

  const statusCls = (s) =>
    ({ VALIDATED: 'bg-green-50 text-green-700', PENDING: 'bg-yellow-50 text-yellow-700',
       IN_PROGRESS: 'bg-blue-50 text-blue-700', CANCELLED: 'bg-red-50 text-red-700' }[s] ?? 'bg-gray-100 text-gray-600');

  return (
    <div className="space-y-6">
      {/* KPI grid — 2 cols on mobile, 3 on sm+ */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        <KpiCard icon={Users} iconBg="bg-blue-50" iconColor="text-blue-600" label="Utilisateurs"
          loading={usersLoading} value={fmtNum(usersStats?.total)} sub={`${fmtNum(usersStats?.active)} actifs`} />
        <KpiCard icon={QrCode} iconBg="bg-purple-50" iconColor="text-purple-600" label="Scans (période)"
          loading={txStatsLoading} value={fmtNum(txStats?.scans)} sub={`${fmtNum(txStats?.validated)} validés`} />
        <KpiCard icon={Banknote} iconBg="bg-green-50" iconColor="text-green-600" label="Gains (période)"
          loading={txStatsLoading} value={fmtCurrency(txStats?.totalGains)} sub={`${fmtNum(txStats?.total)} tx`} />
        <KpiCard icon={TrendingUp} iconBg="bg-indigo-50" iconColor="text-indigo-600" label="Commissions"
          loading={txStatsLoading} value={fmtCurrency(txStats?.totalCommissions)}
          sub={`${fmtNum(txStats?.scans)} scans`} accentClass="border-l-4 border-indigo-400" />
        <KpiCard icon={Tag} iconBg="bg-orange-50" iconColor="text-orange-600" label="Promotions"
          loading={promotionsLoading} value={fmtNum(allPromotions.length)} sub={`${activePromos} actives`} />
        <KpiCard icon={CreditCard} iconBg="bg-yellow-50" iconColor="text-yellow-600" label="Dem. paiement"
          loading={paymentsLoading} value={fmtNum(allPaymentRequests.length)} sub={`${pendingCount} en attente`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card title="Transactions récentes" subtitle={`${recentTx.length} sur la période sélectionnée`}>
          {txLoading ? <Spinner /> : recentTx.length === 0 ? <EmptyState message="Aucune transaction sur cette période" /> : (
            <ul className="divide-y divide-gray-50">
              {recentTx.map((t) => {
                const name = [t.user?.firstName, t.user?.lastName].filter(Boolean).join(' ') || t.user?.email || '—';
                return (
                  <li key={t.id} className="px-5 py-3 hover:bg-gray-50 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {t.pharmacy?.namePharmacy || '—'} · {new Date(t.createdAt).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex flex-col items-end ml-3 shrink-0">
                      <span className="text-sm font-semibold text-gray-900">{fmtCurrency(t.gain)}</span>
                      <span className={`mt-0.5 text-xs px-1.5 py-0.5 rounded-full font-medium ${statusCls(t.status)}`}>{t.status}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card title="Demandes de paiement en attente" subtitle={`${pendingCount} demande(s)`}>
          {paymentsLoading ? <Spinner /> : pendingCount === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-sm text-gray-400 gap-2">
              <CreditCard className="h-8 w-8 text-gray-200" />
              Aucune demande en attente
            </div>
          ) : (
            <ul className="divide-y divide-gray-50">
              {allPaymentRequests.filter((p) => p.status === 'PENDING').slice(0, 6).map((item) => {
                const name = [item.user?.firstName, item.user?.lastName].filter(Boolean).join(' ') || '—';
                return (
                  <li key={item.id} className="px-5 py-3 hover:bg-gray-50 flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{name}</p>
                      <p className="text-xs text-gray-500">{new Date(item.dateDemande).toLocaleDateString('fr-FR')}</p>
                    </div>
                    <span className="text-sm font-bold text-yellow-700 shrink-0">{fmtCurrency(item.amount)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

// ── Tab: Transactions & Revenus ───────────────────────────────────────────────

const TransactionsTab = ({ dailyStats, dailyLoading, topSellers, topPharmacies }) => {
  const chartData = dailyStats.map((d) => ({ ...d, day: shortDay(d.day) }));
  const totalGains = dailyStats.reduce((s, d) => s + (d.totalGain || 0), 0);
  const totalCommissions = dailyStats.reduce((s, d) => s + (d.totalCommission || 0), 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
        <span>Gains sur la période : <strong className="text-gray-900">{fmtCurrency(totalGains)}</strong></span>
        <span>·</span>
        <span>Commissions : <strong className="text-indigo-700">{fmtCurrency(totalCommissions)}</strong></span>
      </div>

      {/* Commissions / jour */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Commissions Mazid / jour</h3>
        <p className="text-xs text-gray-500 mb-4">Commission perçue par Mazid à chaque scan (en DH)</p>
        {dailyLoading ? <Spinner /> : chartData.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="commGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.indigo} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={C.indigo} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9ca3af' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} width={58} tickFormatter={(v) => `${v} DH`} />
              <RTooltip content={<ChartTooltip valFmt={(v) => fmtCurrency(v)} />} />
              <Area type="monotone" dataKey="totalCommission" name="Commissions" stroke={C.indigo} fill="url(#commGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Gains / jour */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">Gains totaux / jour</h3>
        <p className="text-xs text-gray-500 mb-4">Gains distribués aux vendeurs (en DH)</p>
        {dailyLoading ? <Spinner /> : chartData.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gainGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.green} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={C.green} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9ca3af' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} width={58} tickFormatter={(v) => `${v} DH`} />
              <RTooltip content={<ChartTooltip valFmt={(v) => fmtCurrency(v)} />} />
              <Area type="monotone" dataKey="totalGain" name="Gains" stroke={C.green} fill="url(#gainGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Scans / jour */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Nombre de scans / jour</h3>
        {dailyLoading ? <Spinner /> : chartData.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height={200}>
            <RBarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9ca3af' }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} width={30} />
              <RTooltip content={<ChartTooltip valFmt={(v) => `${v} scans`} />} />
              <Bar dataKey="count" name="Scans" fill={C.purple} radius={[3, 3, 0, 0]} maxBarSize={24} />
            </RBarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card title="Top Vendeurs" subtitle="Classement par solde cagnotte (tout temps)">
          {topSellers.length === 0 ? <Spinner /> : (
            <ul className="divide-y divide-gray-50">
              {topSellers.map((seller, idx) => {
                const max = topSellers[0]?.commissionBalance || 1;
                const pct = Math.round((seller.commissionBalance / max) * 100);
                const name = [seller.firstName, seller.lastName].filter(Boolean).join(' ') || seller.email;
                return (
                  <li key={seller.id} className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700 shrink-0">{idx + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline">
                          <span className="text-sm font-medium text-gray-900 truncate">{name}</span>
                          <span className="text-sm font-bold text-green-700 ml-2 shrink-0">{fmtCurrency(seller.commissionBalance)}</span>
                        </div>
                        <p className="text-xs text-gray-400 truncate">{seller.pharmacy?.namePharmacy || seller.email}</p>
                        <div className="mt-1.5 w-full h-1 bg-gray-100 rounded-full">
                          <div className="h-full bg-green-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card title="Top Pharmacies" subtitle="Classement par nombre de scans (tout temps)">
          {topPharmacies.length === 0 ? <Spinner /> : (
            <ul className="divide-y divide-gray-50">
              {topPharmacies.map((ph, idx) => {
                const max = topPharmacies[0]?.scanCount || 1;
                const pct = Math.round((ph.scanCount / max) * 100);
                return (
                  <li key={ph.id} className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700 shrink-0">{idx + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline">
                          <span className="text-sm font-medium text-gray-900 truncate">{ph.namePharmacy}</span>
                          <span className="text-xs font-bold text-purple-700 ml-2 shrink-0">{fmtNum(ph.scanCount)} scans</span>
                        </div>
                        <p className="text-xs text-gray-400">{fmtCurrency(ph.totalGains)}</p>
                        <div className="mt-1.5 w-full h-1 bg-gray-100 rounded-full">
                          <div className="h-full bg-purple-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

// ── Tab: Utilisateurs & Croissance ────────────────────────────────────────────

const UsersTab = ({ usersStats, monthlyUsers, allReferrals, usersLoading, monthlyUsersLoading, referralLoading, dateRange }) => {
  const donutData = [
    { name: 'Vendeurs', value: usersStats?.byType?.SELLER ?? 0, color: USER_TYPE_COLORS.SELLER },
    { name: 'Pharmaciens', value: usersStats?.byType?.PHARMACIST ?? 0, color: USER_TYPE_COLORS.PHARMACIST },
    { name: 'Laboratoires', value: usersStats?.byType?.LABORATORY ?? 0, color: USER_TYPE_COLORS.LABORATORY },
    { name: 'Admins', value: usersStats?.byType?.ADMIN ?? 0, color: USER_TYPE_COLORS.ADMIN },
  ];

  const RADIAN = Math.PI / 180;
  const renderPctLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const stackedData = monthlyUsers.map((m) => ({
    month: m.month.slice(0, 7),
    Vendeurs: m.SELLER || 0,
    Pharmaciens: m.PHARMACIST || 0,
    Labos: m.LABORATORY || 0,
  }));

  // Referral stats (filtered by period)
  const filteredReferrals = (allReferrals || []).filter((r) =>
    inDateRange(r.referralDate, dateRange.dateFrom, dateRange.dateTo)
  );
  const activatedReferrals = filteredReferrals.filter((r) => r.firstScanBonusPaid).length;
  const conversionRate = filteredReferrals.length > 0
    ? Math.round((activatedReferrals / filteredReferrals.length) * 100)
    : 0;

  // Top referrers
  const referrerMap = {};
  for (const r of filteredReferrals) {
    const key = r.referrer?.id;
    if (!key) continue;
    if (!referrerMap[key]) referrerMap[key] = { referrer: r.referrer, count: 0, scans: 0 };
    referrerMap[key].count++;
    referrerMap[key].scans += r.scanCount || 0;
  }
  const topReferrers = Object.values(referrerMap).sort((a, b) => b.count - a.count).slice(0, 5);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Répartition par type (tout temps)</h3>
          {usersLoading ? <Spinner /> : (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={180} height={180}>
                <PieChart>
                  <Pie data={donutData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                    dataKey="value" labelLine={false} label={renderPctLabel}>
                    {donutData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <RTooltip formatter={(v, name) => [fmtNum(v), name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2.5">
                {donutData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: d.color }} />
                      <span className="text-gray-600">{d.name}</span>
                    </div>
                    <span className="font-semibold text-gray-900">{fmtNum(d.value)}</span>
                  </div>
                ))}
                <div className="pt-2 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 font-medium">Total</span>
                    <span className="font-bold text-gray-900">{fmtNum(usersStats?.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Activité des comptes (tout temps)</h3>
          {usersLoading ? <Spinner /> : (
            <div className="space-y-4">
              {[
                { label: 'Comptes actifs', value: usersStats?.active ?? 0, total: usersStats?.total ?? 1, color: 'bg-green-500' },
                { label: 'Comptes inactifs', value: usersStats?.inactive ?? 0, total: usersStats?.total ?? 1, color: 'bg-gray-300' },
                { label: 'Emails vérifiés', value: usersStats?.verified ?? 0, total: usersStats?.total ?? 1, color: 'bg-blue-500' },
                { label: 'Non vérifiés', value: usersStats?.unverified ?? 0, total: usersStats?.total ?? 1, color: 'bg-red-400' },
              ].map((item) => (
                <div key={item.label}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-gray-600">{item.label}</span>
                    <span className="font-medium text-gray-900">
                      {fmtNum(item.value)}{' '}
                      <span className="text-gray-400 font-normal">({Math.round((item.value / item.total) * 100)}%)</span>
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${item.color} rounded-full`} style={{ width: `${Math.round((item.value / item.total) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Stacked bar registrations */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Inscriptions mensuelles par type (12 mois)</h3>
        {monthlyUsersLoading ? <Spinner /> : stackedData.length === 0 ? <EmptyState /> : (
          <ResponsiveContainer width="100%" height={240}>
            <RBarChart data={stackedData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} width={30} />
              <RTooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Vendeurs" stackId="a" fill={USER_TYPE_COLORS.SELLER} />
              <Bar dataKey="Pharmaciens" stackId="a" fill={USER_TYPE_COLORS.PHARMACIST} />
              <Bar dataKey="Labos" stackId="a" fill={USER_TYPE_COLORS.LABORATORY} radius={[3, 3, 0, 0]} />
            </RBarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Monthly table */}
      {!monthlyUsersLoading && monthlyUsers.length > 0 && (
        <Card title="Détail mensuel">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <th className="px-5 py-3 text-left">Mois</th>
                  <th className="px-4 py-3 text-right text-orange-600">Vendeurs</th>
                  <th className="px-4 py-3 text-right text-green-600">Pharmaciens</th>
                  <th className="px-4 py-3 text-right text-purple-600">Labos</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {[...monthlyUsers].reverse().map((m) => (
                  <tr key={m.month} className="hover:bg-gray-50">
                    <td className="px-5 py-2.5 text-gray-700 font-medium">{m.month.slice(0, 7)}</td>
                    <td className="px-4 py-2.5 text-right text-orange-600 font-medium">{m.SELLER || 0}</td>
                    <td className="px-4 py-2.5 text-right text-green-600 font-medium">{m.PHARMACIST || 0}</td>
                    <td className="px-4 py-2.5 text-right text-purple-600 font-medium">{m.LABORATORY || 0}</td>
                    <td className="px-4 py-2.5 text-right font-bold text-gray-900">{m.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Parrainage ── */}
      <div className="border-t pt-5">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <GitBranch className="h-5 w-5 text-blue-600" />
          Parrainage — période sélectionnée
        </h3>

        {referralLoading ? <Spinner /> : (
          <>
            {/* Referral KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
              <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-blue-400">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total parrainages</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{fmtNum(filteredReferrals.length)}</p>
                <p className="text-xs text-gray-400 mt-0.5">sur la période</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-green-400">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Parrainages activés</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{fmtNum(activatedReferrals)}</p>
                <p className="text-xs text-gray-400 mt-0.5">1er scan effectué</p>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-orange-400">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Taux de conversion</p>
                <p className="mt-1 text-2xl font-bold text-gray-900">{conversionRate}%</p>
                <p className="text-xs text-gray-400 mt-0.5">activés / total</p>
              </div>
            </div>

            {/* Top referrers */}
            {topReferrers.length > 0 && (
              <Card title="Top Parrains" subtitle="Classement par nombre de filleuls sur la période">
                <ul className="divide-y divide-gray-50">
                  {topReferrers.map((item, idx) => {
                    const max = topReferrers[0]?.count || 1;
                    const pct = Math.round((item.count / max) * 100);
                    const name = [item.referrer?.firstName, item.referrer?.lastName].filter(Boolean).join(' ') || item.referrer?.email || '—';
                    return (
                      <li key={item.referrer.id} className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 shrink-0">{idx + 1}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-baseline">
                              <span className="text-sm font-medium text-gray-900 truncate">{name}</span>
                              <span className="text-sm font-bold text-blue-700 ml-2 shrink-0">{item.count} filleul{item.count > 1 ? 's' : ''}</span>
                            </div>
                            <p className="text-xs text-gray-400">{item.scans} scans générés</p>
                            <div className="mt-1.5 w-full h-1 bg-gray-100 rounded-full">
                              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            )}

            {filteredReferrals.length === 0 && (
              <EmptyState message="Aucun parrainage sur cette période" />
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Tab: Rétention & Géographie ───────────────────────────────────────────────

const RetentionTab = ({ churnMetrics, churnLoading, regionData, regionLoading, cohortData, cohortLoading, churnProfile, churnProfileLoading }) => {
  const [selectedChurnStatus, setSelectedChurnStatus] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const apolloClient = useApolloClient();

  const { data: churnUsersData, loading: churnUsersLoading } = useQuery(GET_USERS_BY_CHURN_STATUS, {
    variables: { status: selectedChurnStatus },
    skip: !selectedChurnStatus,
    fetchPolicy: 'cache-and-network',
  });

  const CHURN_EXPORT_HEADERS = [
    'Prénom', 'Nom', 'Email', 'Téléphone', 'Type',
    'Scans', 'Dernier scan', 'Solde commission (DH)', 'Actif',
  ];
  const userToRow = (u) => [
    u.firstName || '',
    u.lastName || '',
    u.email || '',
    u.tel || '',
    u.userType === 'SELLER' ? 'Vendeur' : u.userType === 'PHARMACIST' ? 'Pharmacien' : u.userType || '',
    String(u.scanCount ?? 0),
    u.lastScan ? u.lastScan.slice(0, 10) : '',
    u.commissionBalance != null ? u.commissionBalance.toFixed(2) : '0.00',
    u.isActive ? 'Oui' : 'Non',
  ];

  // Export the currently loaded user list (single category)
  const exportCurrentList = () => {
    const users = churnUsersData?.usersByChurnStatus?.users || [];
    const label = CHURN_STATUS_KEY[selectedChurnStatus] || selectedChurnStatus;
    exportToExcel(CHURN_EXPORT_HEADERS, users.map(userToRow), `retention_${selectedChurnStatus}.xls`);
  };

  // Fetch all 4 categories and export as a multi-sheet Excel
  const exportAllCategories = async () => {
    setExportingAll(true);
    try {
      const statuses = [
        { key: 'active',  name: 'Actifs'    },
        { key: 'atRisk',  name: 'À risque'  },
        { key: 'churned', name: 'Churnés'   },
        { key: 'ghost',   name: 'Fantômes'  },
      ];
      const results = await Promise.all(
        statuses.map(({ key }) =>
          apolloClient.query({
            query: GET_USERS_BY_CHURN_STATUS,
            variables: { status: key },
            fetchPolicy: 'cache-first',
          })
        )
      );
      const sheets = statuses.map(({ key, name }, i) => ({
        name,
        headers: CHURN_EXPORT_HEADERS,
        rows: (results[i].data?.usersByChurnStatus?.users || []).map(userToRow),
      }));
      exportMultiSheetExcel(sheets, `retention_utilisateurs_${new Date().toISOString().slice(0, 10)}.xls`);
    } catch {
      // silent — user will notice nothing downloaded
    } finally {
      setExportingAll(false);
    }
  };

  const churnDonut = churnMetrics
    ? [
        { name: 'Actifs', value: churnMetrics.active, color: C.green },
        { name: 'À risque', value: churnMetrics.atRisk, color: C.yellow },
        { name: 'Churnés', value: churnMetrics.churned, color: C.red },
        { name: 'Fantômes', value: churnMetrics.ghost, color: C.gray },
      ]
    : [];

  const maxCohortCols = cohortData?.length > 0
    ? Math.max(...cohortData.map((r) => r.monthlyRetention.length))
    : 0;

  const CHURN_STATUS_KEY = { active: 'Actifs', atRisk: 'À risque', churned: 'Churnés', ghost: 'Fantômes' };

  const churnCards = churnMetrics
    ? [
        { statusKey: 'active', label: 'Actifs', value: churnMetrics.active, sub: 'scan < 30j', Icon: UserCheck, bg: 'bg-green-50', fg: 'text-green-600', border: 'border-l-4 border-green-400', ring: 'ring-2 ring-green-400' },
        { statusKey: 'atRisk', label: 'À risque', value: churnMetrics.atRisk, sub: '30 – 60j', Icon: AlertTriangle, bg: 'bg-yellow-50', fg: 'text-yellow-600', border: 'border-l-4 border-yellow-400', ring: 'ring-2 ring-yellow-400' },
        { statusKey: 'churned', label: 'Churnés', value: churnMetrics.churned, sub: 'scan > 60j', Icon: UserX, bg: 'bg-red-50', fg: 'text-red-600', border: 'border-l-4 border-red-400', ring: 'ring-2 ring-red-400' },
        { statusKey: 'ghost', label: 'Fantômes', value: churnMetrics.ghost, sub: 'jamais scanné', Icon: UserMinus, bg: 'bg-gray-50', fg: 'text-gray-500', border: 'border-l-4 border-gray-300', ring: 'ring-2 ring-gray-400' },
      ]
    : [];

  const handleCardClick = (statusKey) => {
    setSelectedChurnStatus((prev) => (prev === statusKey ? null : statusKey));
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const churnUsers = churnUsersData?.usersByChurnStatus?.users || [];
  const churnUsersTotal = churnUsersData?.usersByChurnStatus?.total ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-blue-50 rounded-lg text-xs text-blue-700">
        <span className="font-medium">ℹ️</span>
        <span className="flex-1">Les métriques de rétention sont calculées sur toute la période — le filtre de date ne s'applique pas à cet onglet.
        <span className="ml-1 font-medium">Cliquez sur une carte pour voir les utilisateurs correspondants.</span></span>
        <button
          onClick={exportAllCategories}
          disabled={exportingAll || churnLoading}
          className="flex items-center gap-1.5 ml-auto px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Exporter les 4 catégories en un seul fichier Excel (4 onglets)"
        >
          {exportingAll
            ? <Loader2 size={12} className="animate-spin" />
            : <Download size={12} />
          }
          Exporter tout (Excel)
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {churnLoading
          ? [1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm p-5 h-28 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gray-300" />
              </div>
            ))
          : churnCards.map((card) => {
              const isSelected = selectedChurnStatus === card.statusKey;
              return (
                <button
                  key={card.label}
                  onClick={() => handleCardClick(card.statusKey)}
                  className={`bg-white rounded-xl shadow-sm p-5 text-left transition-all ${card.border} ${
                    isSelected ? `${card.ring} scale-[1.02] shadow-md` : 'hover:shadow-md hover:scale-[1.01]'
                  } cursor-pointer`}
                >
                  <div className={`${card.bg} rounded-lg p-2.5 w-fit`}>
                    <card.Icon className={`h-5 w-5 ${card.fg}`} />
                  </div>
                  <p className="mt-3 text-xs font-medium text-gray-500 uppercase tracking-wide">{card.label}</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900">{fmtNum(card.value)}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{card.sub}</p>
                  {isSelected && (
                    <p className="text-xs font-semibold mt-2 text-gray-500">Voir la liste ↓</p>
                  )}
                </button>
              );
            })}
      </div>

      {/* Users list for selected churn status */}
      {selectedChurnStatus && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Utilisateurs — {CHURN_STATUS_KEY[selectedChurnStatus]}
              </h3>
              {!churnUsersLoading && (
                <p className="text-xs text-gray-400 mt-0.5">{churnUsersTotal} utilisateur{churnUsersTotal !== 1 ? 's' : ''}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!churnUsersLoading && churnUsers.length > 0 && (
                <button
                  onClick={exportCurrentList}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                  title={`Exporter les ${churnUsersTotal} utilisateurs en Excel`}
                >
                  <Download size={12} /> Excel ({churnUsersTotal})
                </button>
              )}
              <button onClick={() => setSelectedChurnStatus(null)} className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100">
                Fermer ✕
              </button>
            </div>
          </div>
          {churnUsersLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-300" /></div>
          ) : churnUsers.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-400">Aucun utilisateur dans cette catégorie</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    <th className="px-5 py-3 text-left">Utilisateur</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-right">Scans</th>
                    <th className="px-4 py-3 text-right">Dernier scan</th>
                    <th className="px-4 py-3 text-right">Solde</th>
                    <th className="px-4 py-3 text-center">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {churnUsers.map((user) => (
                    <tr
                      key={user.id}
                      onClick={() => handleUserClick(user)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-3">
                        <div className="font-medium text-gray-900">
                          {[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email}
                        </div>
                        <div className="text-xs text-gray-400">{user.email}</div>
                        {user.tel && <div className="text-xs text-gray-400">{user.tel}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: `${USER_TYPE_COLORS[user.userType]}22`, color: USER_TYPE_COLORS[user.userType] }}>
                          {user.userType === 'SELLER' ? 'Vendeur' : user.userType === 'PHARMACIST' ? 'Pharmacien' : user.userType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">{fmtNum(user.scanCount)}</td>
                      <td className="px-4 py-3 text-right text-gray-600 text-xs">
                        {user.lastScan ? fmtDate(user.lastScan.slice(0, 10)) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900">{fmtCurrency(user.commissionBalance)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                          user.isActive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                        }`}>
                          {user.isActive ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!churnLoading && churnMetrics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col items-center justify-center">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Taux de churn</p>
            <div className="text-5xl font-black text-red-600">{fmtPct(churnMetrics.churnRate)}</div>
            <p className="text-xs text-gray-400 mt-2 text-center">des vendeurs & pharmaciens<br />n'ont pas scanné depuis +60j</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5 lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Distribution des utilisateurs actifs/pharma</h3>
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={160} height={160}>
                <PieChart>
                  <Pie data={churnDonut} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={2}>
                    {churnDonut.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <RTooltip formatter={(v, name) => [fmtNum(v), name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2.5">
                {churnDonut.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-gray-600">{d.name}</span>
                    </div>
                    <div className="text-right">
                      <span className="font-semibold text-gray-900">{fmtNum(d.value)}</span>
                      <span className="text-xs text-gray-400 ml-1">({Math.round((d.value / (churnMetrics.total || 1)) * 100)}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {!churnProfileLoading && churnProfile?.length > 0 && (
        <Card title="Profil des utilisateurs churnés (>60j)" subtitle="Comportement avant le churn">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <th className="px-5 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-right">Nb</th>
                  <th className="px-4 py-3 text-right">Scans moy.</th>
                  <th className="px-4 py-3 text-right">Gains moy.</th>
                  <th className="px-4 py-3 text-right">Durée moy.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {churnProfile.map((row) => (
                  <tr key={row.userType} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: `${USER_TYPE_COLORS[row.userType]}22`, color: USER_TYPE_COLORS[row.userType] }}>
                        {row.userType === 'SELLER' ? 'Vendeur' : row.userType === 'PHARMACIST' ? 'Pharmacien' : row.userType}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900">{fmtNum(row.count)}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{row.avgScansBeforeChurn?.toFixed(1)}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{fmtCurrency(row.avgGainBeforeChurn)}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{row.avgDaysActive?.toFixed(0)} j</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Matrice de rétention par cohorte" subtitle="% d'utilisateurs actifs au mois M+N après leur inscription">
        {cohortLoading ? <Spinner /> : !cohortData?.length ? (
          <EmptyState message="Données de cohorte insuffisantes" />
        ) : (
          <div className="overflow-x-auto p-5">
            <table className="text-xs border-collapse">
              <thead>
                <tr>
                  <th className="text-left pr-4 py-1 text-gray-500 font-medium whitespace-nowrap">Cohorte</th>
                  <th className="px-3 py-1 text-gray-500 font-medium text-center whitespace-nowrap">N</th>
                  {Array.from({ length: maxCohortCols }, (_, i) => (
                    <th key={i} className="px-2 py-1 text-gray-500 font-medium text-center whitespace-nowrap">M+{i}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohortData.map((row) => (
                  <tr key={row.cohortMonth}>
                    <td className="pr-4 py-1 text-gray-700 font-medium whitespace-nowrap">{row.cohortMonth}</td>
                    <td className="px-3 py-1 text-center text-gray-600 font-medium whitespace-nowrap">{row.cohortSize}</td>
                    {row.monthlyRetention.map((pct, i) => (
                      <td key={i} className="px-2 py-1 text-center whitespace-nowrap rounded"
                        style={{ backgroundColor: retentionBg(pct), color: retentionFg(pct), fontWeight: i === 0 ? 700 : 500 }}>
                        {pct > 0 ? `${pct}%` : '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {!regionLoading && regionData?.length > 0 && (
        <>
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Utilisateurs par région</h3>
            <ResponsiveContainer width="100%" height={Math.max(240, regionData.length * 38)}>
              <RBarChart data={[...regionData].reverse()} layout="vertical" margin={{ top: 0, right: 60, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis type="category" dataKey="region" tick={{ fontSize: 11, fill: '#374151' }} width={110} />
                <RTooltip content={<ChartTooltip valFmt={(v) => fmtNum(v)} />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="activeUsers" name="Actifs" fill={C.green} stackId="a" />
                <Bar dataKey="churnedUsers" name="Churnés" fill={C.red} stackId="a" />
                <Bar dataKey="ghostUsers" name="Fantômes" fill={C.gray} stackId="a" radius={[4, 4, 0, 0]} />
              </RBarChart>
            </ResponsiveContainer>
          </div>

          <Card title="Détail par région">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    <th className="px-5 py-3 text-left">Région</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3 text-right text-green-600">Actifs</th>
                    <th className="px-4 py-3 text-right text-red-600">Churnés</th>
                    <th className="px-4 py-3 text-right">Scans</th>
                    <th className="px-4 py-3 text-right">Gains</th>
                    <th className="px-4 py-3 text-right">Churn %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {regionData.map((r) => (
                    <tr key={r.region} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-medium text-gray-900">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />{r.region}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{fmtNum(r.totalUsers)}</td>
                      <td className="px-4 py-3 text-right font-medium text-green-600">{fmtNum(r.activeUsers)}</td>
                      <td className="px-4 py-3 text-right font-medium text-red-500">{fmtNum(r.churnedUsers)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{fmtNum(r.totalScans)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{fmtCurrency(r.totalGains)}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                          r.churnRate >= 50 ? 'bg-red-50 text-red-700'
                          : r.churnRate >= 25 ? 'bg-yellow-50 text-yellow-700'
                          : 'bg-green-50 text-green-700'
                        }`}>
                          {fmtPct(r.churnRate)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {showUserModal && selectedUser && (
        <UserDetailModal
          user={selectedUser}
          isOpen={showUserModal}
          onClose={() => { setShowUserModal(false); setSelectedUser(null); }}
        />
      )}
    </div>
  );
};

// ── Tab: Promotions & Paiements ───────────────────────────────────────────────

const PromotionsTab = ({ allPromotions, allPaymentRequests, promotionsLoading, paymentsLoading, dateRange }) => {
  const promoData = [
    { name: 'Actives', value: allPromotions.filter((p) => p.status === 'ACTIVE').length, color: C.green },
    { name: 'En attente', value: allPromotions.filter((p) => p.status === 'PENDING').length, color: C.yellow },
    { name: 'Expirées', value: allPromotions.filter((p) => p.status === 'EXPIRED').length, color: C.gray },
  ];

  const filteredPayments = allPaymentRequests.filter((p) =>
    inDateRange(p.dateDemande, dateRange.dateFrom, dateRange.dateTo)
  );

  const payGroups = [
    { label: 'En attente', status: 'PENDING', color: 'text-yellow-600', bg: 'bg-yellow-50' },
    { label: 'En cours', status: 'IN_PROGRESS', color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Validées', status: 'VALIDATED', color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Annulées', status: 'CANCELLED', color: 'text-red-600', bg: 'bg-red-50' },
  ];

  const pendingItems = filteredPayments.filter((p) => p.status === 'PENDING');

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">État des promotions (tout temps)</h3>
          {promotionsLoading ? <Spinner /> : (
            <div className="flex items-center gap-6">
              <ResponsiveContainer width={160} height={160}>
                <PieChart>
                  <Pie data={promoData.filter((d) => d.value > 0)} cx="50%" cy="50%"
                    innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={2}>
                    {promoData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <RTooltip formatter={(v, name) => [v, name]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-3">
                {promoData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: d.color }} />
                      <span className="text-gray-600">{d.name}</span>
                    </div>
                    <span className="text-lg font-bold text-gray-900">{d.value}</span>
                  </div>
                ))}
                <div className="pt-2 border-t flex justify-between text-sm">
                  <span className="text-gray-500">Total</span>
                  <span className="font-bold text-gray-900">{allPromotions.length}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Demandes de paiement par statut (période)</h3>
          {paymentsLoading ? <Spinner /> : (
            <div className="space-y-3">
              {payGroups.map((g) => {
                const items = filteredPayments.filter((p) => p.status === g.status);
                return (
                  <div key={g.label} className="flex items-center justify-between">
                    <span className={`text-sm px-2 py-0.5 rounded-md font-medium ${g.bg} ${g.color}`}>{g.label}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-gray-900">{items.length}</span>
                      <span className="text-xs text-gray-400">{fmtCurrency(items.reduce((s, p) => s + (p.amount || 0), 0))}</span>
                    </div>
                  </div>
                );
              })}
              <div className="pt-3 border-t flex justify-between text-sm">
                <span className="text-gray-500">Total période</span>
                <span className="font-bold text-gray-900">
                  {filteredPayments.length} · {fmtCurrency(filteredPayments.reduce((s, p) => s + (p.amount || 0), 0))}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <Card title="Demandes en attente (période)" subtitle={`${pendingItems.length} demandes nécessitent une action`}>
        {paymentsLoading ? <Spinner /> : pendingItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-sm text-gray-400 gap-2">
            <CreditCard className="h-8 w-8 text-gray-200" />
            Aucune demande en attente sur cette période
          </div>
        ) : (
          <ul className="divide-y divide-gray-50">
            {pendingItems.map((item) => {
              const name = [item.user?.firstName, item.user?.lastName].filter(Boolean).join(' ') || '—';
              return (
                <li key={item.id} className="px-5 py-3 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{name}</p>
                    <p className="text-xs text-gray-500">{new Date(item.dateDemande).toLocaleDateString('fr-FR')}</p>
                  </div>
                  <span className="text-sm font-bold text-yellow-700">{fmtCurrency(item.amount)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
};

// ── Main Dashboard Component ──────────────────────────────────────────────────

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Period filter state
  const [filterMode, setFilterMode] = useState('preset');
  const [presetDays, setPresetDays] = useState(30);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [qNum, setQNum] = useState(1);
  const [qYear, setQYear] = useState(new Date().getFullYear());

  const handleModeChange = (mode) => setFilterMode(mode);
  const handlePresetDays = (days) => setPresetDays(days);
  const handleCustomFrom = (val) => setCustomFrom(val);
  const handleCustomTo = (val) => setCustomTo(val);
  const handleQNum = (n) => setQNum(n);
  const handleQYear = (y) => setQYear(y);

  const dateRange = computeDateRange(filterMode, presetDays, customFrom, customTo, qNum, qYear);

  // Core queries (always)
  const { data: usersStatsData, loading: usersLoading } = useQuery(GET_USERS_STATISTICS, { fetchPolicy: 'cache-and-network' });
  const { data: txStatsData, loading: txStatsLoading } = useQuery(GET_TRANSACTION_STATISTICS, {
    variables: { dateFrom: dateRange.dateFrom, dateTo: dateRange.dateTo },
    fetchPolicy: 'cache-and-network',
  });
  const { data: txData, loading: txLoading } = useQuery(GET_RECENT_TRANSACTIONS, { fetchPolicy: 'cache-and-network' });
  const { data: promotionsData, loading: promotionsLoading } = useQuery(GET_PROMOTIONS_SUMMARY, { fetchPolicy: 'cache-and-network' });
  const { data: paymentReqData, loading: paymentsLoading } = useQuery(GET_PAYMENT_REQUESTS_SUMMARY, { fetchPolicy: 'cache-and-network' });
  const { data: topSellersData } = useQuery(GET_TOP_SELLERS, {
    variables: { input: { page: 1, limit: 8, sortBy: 'commissionBalance', sortOrder: 'desc', userType: 'SELLER' } },
    fetchPolicy: 'cache-and-network',
  });
  const { data: topPharmaciesData } = useQuery(GET_TOP_PHARMACIES, { fetchPolicy: 'cache-and-network' });
  const { data: dailyStatsData, loading: dailyLoading } = useQuery(GET_DAILY_STATS, {
    variables: { dateFrom: dateRange.dateFrom, dateTo: dateRange.dateTo },
    fetchPolicy: 'cache-and-network',
  });
  const { data: monthlyUsersData, loading: monthlyUsersLoading } = useQuery(GET_MONTHLY_USERS, {
    variables: { months: 12 },
    fetchPolicy: 'cache-and-network',
  });
  const { data: pendingFraudData } = useQuery(GET_PENDING_FRAUD_COUNT, { fetchPolicy: 'cache-and-network' });

  // Lazy queries
  const isRetention = activeTab === 'retention';
  const { data: churnData, loading: churnLoading } = useQuery(GET_USER_CHURN_METRICS, { skip: !isRetention, fetchPolicy: 'cache-and-network' });
  const { data: regionData, loading: regionLoading } = useQuery(GET_USERS_BY_REGION, { skip: !isRetention, fetchPolicy: 'cache-and-network' });
  const { data: churnProfileData, loading: churnProfileLoading } = useQuery(GET_CHURN_PROFILE, { skip: !isRetention, fetchPolicy: 'cache-and-network' });
  const { data: cohortData, loading: cohortLoading } = useQuery(GET_RETENTION_COHORTS, {
    variables: { months: 6 }, skip: !isRetention, fetchPolicy: 'cache-and-network',
  });
  const isUsers = activeTab === 'users';
  const { data: referralData, loading: referralLoading } = useQuery(GET_ALL_REFERRALS, {
    skip: !isUsers, fetchPolicy: 'cache-and-network',
  });

  const usersStats = usersStatsData?.usersStatistics;
  const txStats = txStatsData?.transactionStatistics;
  const allTransactions = txData?.transactions || [];
  const allPromotions = promotionsData?.allPromotions || [];
  const allPaymentRequests = paymentReqData?.paymentRequests || [];
  const topSellers = topSellersData?.usersPaginated?.users || [];
  const topPharmacies = topPharmaciesData?.topPharmaciesByScans || [];
  const dailyStats = dailyStatsData?.dailyTransactionStats || [];
  const monthlyUsers = monthlyUsersData?.monthlyUserRegistrations || [];
  const allReferrals = referralData?.allReferrals || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Tableau de bord</h2>

        {(pendingFraudData?.pendingFraudCasesCount ?? 0) > 0 && (
          <a href="/admin/fraud-cases" className="mb-4 flex items-center gap-3 bg-red-600 text-white rounded-xl px-5 py-3 hover:bg-red-700 transition-colors">
            <ShieldAlert className="h-5 w-5 shrink-0" />
            <span className="text-sm font-semibold">{pendingFraudData.pendingFraudCasesCount} cas de fraude en attente</span>
            <span className="ml-auto text-xs underline opacity-80">Voir →</span>
          </a>
        )}

        {/* Global period filter */}
        <FilterBar
          filterMode={filterMode} onModeChange={handleModeChange}
          presetDays={presetDays} onPresetDays={handlePresetDays}
          customFrom={customFrom} onCustomFrom={handleCustomFrom}
          customTo={customTo} onCustomTo={handleCustomTo}
          qNum={qNum} onQNum={handleQNum}
          qYear={qYear} onQYear={handleQYear}
        />

        {/* Tab navigation */}
        <div className="flex overflow-x-auto gap-1 mb-6 bg-white rounded-xl shadow-sm p-1">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <OverviewTab
            usersStats={usersStats} txStats={txStats}
            allTransactions={allTransactions} allPaymentRequests={allPaymentRequests} allPromotions={allPromotions}
            usersLoading={usersLoading} txStatsLoading={txStatsLoading} txLoading={txLoading}
            paymentsLoading={paymentsLoading} promotionsLoading={promotionsLoading}
            dateRange={dateRange}
          />
        )}
        {activeTab === 'transactions' && (
          <TransactionsTab
            dailyStats={dailyStats} dailyLoading={dailyLoading}
            topSellers={topSellers} topPharmacies={topPharmacies}
          />
        )}
        {activeTab === 'users' && (
          <UsersTab
            usersStats={usersStats} monthlyUsers={monthlyUsers} allReferrals={allReferrals}
            usersLoading={usersLoading} monthlyUsersLoading={monthlyUsersLoading} referralLoading={referralLoading}
            dateRange={dateRange}
          />
        )}
        {activeTab === 'retention' && (
          <RetentionTab
            churnMetrics={churnData?.userChurnMetrics} churnLoading={churnLoading}
            regionData={regionData?.usersByRegion} regionLoading={regionLoading}
            cohortData={cohortData?.retentionCohorts} cohortLoading={cohortLoading}
            churnProfile={churnProfileData?.churnProfileByType} churnProfileLoading={churnProfileLoading}
          />
        )}
        {activeTab === 'promotions' && (
          <PromotionsTab
            allPromotions={allPromotions} allPaymentRequests={allPaymentRequests}
            promotionsLoading={promotionsLoading} paymentsLoading={paymentsLoading}
            dateRange={dateRange}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
